/**
 * useVehicles Hook
 * React Query hooks for vehicle management
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { vehicleService } from '../services/vehicle.service';
import type {
  VehicleStatus,
  CreateVehicleRequest,
  AssignDriverRequest,
  RecordMaintenanceRequest,
  UpdateOdometerRequest,
  UpdateDOTRequest,
} from '../services/vehicle.service';

/**
 * Query key factory
 */
const vehicleKeys = {
  all: ['vehicles'] as const,
  lists: () => [...vehicleKeys.all, 'list'] as const,
  list: (filters?: { status?: VehicleStatus; maintenanceDue?: boolean }) =>
    [...vehicleKeys.lists(), filters] as const,
  details: () => [...vehicleKeys.all, 'detail'] as const,
  detail: (id: string) => [...vehicleKeys.details(), id] as const,
};

/**
 * Hook to list vehicles with optional filters
 */
export function useVehicles(filters?: { status?: VehicleStatus; maintenanceDue?: boolean }) {
  return useQuery({
    queryKey: vehicleKeys.list(filters),
    queryFn: () => vehicleService.list(filters),
  });
}

/**
 * Hook to get a single vehicle by ID
 */
export function useVehicle(id: string) {
  return useQuery({
    queryKey: vehicleKeys.detail(id),
    queryFn: () => vehicleService.getById(id),
    enabled: !!id,
  });
}

/**
 * Hook to create a vehicle
 */
export function useCreateVehicle() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateVehicleRequest) => vehicleService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: vehicleKeys.lists() });
    },
  });
}

/**
 * Hook to update a vehicle
 */
export function useUpdateVehicle() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CreateVehicleRequest> }) =>
      vehicleService.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: vehicleKeys.lists() });
      queryClient.invalidateQueries({ queryKey: vehicleKeys.detail(variables.id) });
    },
  });
}

/**
 * Hook to delete a vehicle
 */
export function useDeleteVehicle() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => vehicleService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: vehicleKeys.lists() });
    },
  });
}

/**
 * Hook to assign a driver to a vehicle
 */
export function useAssignDriver() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ vehicleId, data }: { vehicleId: string; data: AssignDriverRequest }) =>
      vehicleService.assignDriver(vehicleId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: vehicleKeys.lists() });
      queryClient.invalidateQueries({ queryKey: vehicleKeys.detail(variables.vehicleId) });
      // Also invalidate drivers since assignment affects both
      queryClient.invalidateQueries({ queryKey: ['drivers'] });
    },
  });
}

/**
 * Hook to unassign a driver from a vehicle
 */
export function useUnassignDriver() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (vehicleId: string) => vehicleService.unassignDriver(vehicleId),
    onSuccess: (_, vehicleId) => {
      queryClient.invalidateQueries({ queryKey: vehicleKeys.lists() });
      queryClient.invalidateQueries({ queryKey: vehicleKeys.detail(vehicleId) });
      queryClient.invalidateQueries({ queryKey: ['drivers'] });
    },
  });
}

/**
 * Hook to record maintenance
 */
export function useRecordMaintenance() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ vehicleId, data }: { vehicleId: string; data: RecordMaintenanceRequest }) =>
      vehicleService.recordMaintenance(vehicleId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: vehicleKeys.lists() });
      queryClient.invalidateQueries({ queryKey: vehicleKeys.detail(variables.vehicleId) });
    },
  });
}

/**
 * Hook to complete maintenance
 */
export function useCompleteMaintenance() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (vehicleId: string) => vehicleService.completeMaintenance(vehicleId),
    onSuccess: (_, vehicleId) => {
      queryClient.invalidateQueries({ queryKey: vehicleKeys.lists() });
      queryClient.invalidateQueries({ queryKey: vehicleKeys.detail(vehicleId) });
    },
  });
}

/**
 * Hook to update odometer
 */
export function useUpdateOdometer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ vehicleId, data }: { vehicleId: string; data: UpdateOdometerRequest }) =>
      vehicleService.updateOdometer(vehicleId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: vehicleKeys.lists() });
      queryClient.invalidateQueries({ queryKey: vehicleKeys.detail(variables.vehicleId) });
    },
  });
}

/**
 * Hook to update DOT inspection
 */
export function useUpdateDOT() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ vehicleId, data }: { vehicleId: string; data: UpdateDOTRequest }) =>
      vehicleService.updateDOT(vehicleId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: vehicleKeys.lists() });
      queryClient.invalidateQueries({ queryKey: vehicleKeys.detail(variables.vehicleId) });
    },
  });
}
